import React from "react";
import { Button, Checkbox } from "antd";
import { Transaction } from "common/interfaces";
import { Link } from "react-router-dom";
import { UTCToJapanTime } from "common/helper/time.helper";
import {
  TransactionStatusEnum,
  TransactionStatusLabel,
} from "common/enums/TransactionStatus.enum";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { UserTypeEnum } from "common/enums/UserType.enum";

interface TransactionItemProps<T extends Transaction> {
  transaction: T;
  checked: boolean;
  onSelect: (code: string, checked: boolean) => void;
  hideSelect?: boolean;
}

export function TransactionItem<T extends Transaction>({
  transaction,
  checked,
  onSelect,
  hideSelect,
}: TransactionItemProps<T>) {
  const { userData } = useSelector((state: RootState) => state.auth);
  const timeJapan = UTCToJapanTime(transaction.created_at);

  return (
    <div className="self-stretch bg-white rounded-md flex flex-col justify-start items-start overflow-hidden">
      <div
        data-プロパティ1="base + checkbox"
        className="self-stretch min-h-10 px-12 py-2.5 relative bg-primary inline-flex justify-center items-center gap-2.5"
      >
        <div className="flex-1 text-center justify-start text-white text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
          取引ID #{transaction.product.code}
        </div>
        <div
          data-プロパティ1="active - border"
          className="w-7 h-7 py-[3px] left-[10px] top-[6px] absolute bg-primary rounded-md inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden"
        >
          {!hideSelect && (
            <Checkbox
              checked={checked}
              onChange={(e) => onSelect(transaction.code, e.target.checked)}
            />
          )}
        </div>
      </div>
      <div className="self-stretch min-h-10 p-5 bg-white rounded-bl-md rounded-br-md border-l border-r border-b border-neutral-300 flex flex-col justify-center items-center gap-5">
        <div className="self-stretch inline-flex justify-start items-start gap-3.5 flex-wrap content-start">
          <div className="min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              出品ID
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              <Link
                to={`/seller/product/${transaction.product.code}`}
                className="text-primary"
              >
                #{transaction.product.code}
              </Link>
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
                商品名
              </div>
              <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
                {transaction.product.product_name}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              {userData?.type === UserTypeEnum.COMPANY_BUYER
                ? "売り手企業"
                : "買い手企業"}
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {userData?.type === UserTypeEnum.COMPANY_BUYER
                ? transaction.seller.company_name
                : transaction.buyer.company_name}
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
                ステータス
              </div>
              <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
                {transaction.deleted_at
                  ? "削除された"
                  : TransactionStatusLabel[
                      transaction.status as TransactionStatusEnum
                    ]}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
                最終更新
              </div>
              <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
                {`${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`}
              </div>
            </div>
          </div>
        </div>
        <Link
          to={`/${userData?.type === UserTypeEnum.COMPANY_BUYER ? "buyer" : "seller"}/product/${transaction.product.code}`}
          className="w-full"
        >
          <Button
            block
            type="primary"
            htmlType="button"
            className="w-full min-h-[50px] bg-primary-light text-primary text-[16px] font-bold"
          >
            取引画面へ
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default TransactionItem;
